import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQueryClient,
} from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getSectionSearchList } from '../utils/urls';

import {
  QuestionSearchParams,
  SectionSearchHeader,
  SortItem,
} from 'containers/AccountsList/AdvancedSearch/advancedSearchTypes';
import { ErrorModal } from 'types/modalTypes';

const pageLength = 100;

function getSortKey(sortKey?: string) {
  if (!sortKey) return '';
  const selected = sortKey?.toLowerCase();
  let key = '';
  if (selected === 'cid' || selected === 'pid' || selected === 'sid') {
    key = `${sortKey}`;
  } else if (selected === 'account name') {
    key = 'AccountName';
  } else if (selected === 'section title') {
    key = 'SectionTitle';
  } else if (selected === 'form id') {
    key = 'FormId';
  } else if (selected === 'form notes') {
    key = 'FormNotes';
  } else if (selected === 'proteus account id') {
    key = 'AccountId';
  }
  return key;
}

async function fetchAdvancedSectionSearches({
  requestData,
}: TVariables): Promise<TResult> {
  const response = await fetch(getSectionSearchList, {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchAdvancedSectionSearches, 500);

type TError = ErrorModal;

type resultType = {
  Data: Array<SectionSearchHeader>;
  Draw: number;
  FilteredRecords: number;
  IsAutoRefreshEnabled: boolean;
  IsSuccess: boolean;
  PageNumber: number;
  StatusCode: number;
  TotalRecords: number;
};

type TResult = {
  Data: resultType;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  requestData: any;
};

function prepareData({
  start,
  searchList,
  includeGlobal,
  includeInactive,
  includeHidden,
  columnsSearch,
  currentSortItem,
}: { start: number } & QuestionSearchParams & {
    columnsSearch?: Record<string, string>;
    currentSortItem?: SortItem;
  }) {
  return {
    PageNumber: start,
    draw: 1,
    PageSize: 100,
    columns: [
      {
        data: 'Id',
        name: 'Id',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: '',
        },
      },
      {
        data: 'AccountId',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['Proteus Account ID']
              ? columnsSearch['Proteus Account ID']
              : ''
          }`,
        },
      },
      {
        data: 'CID',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['CID'] ? columnsSearch['CID'] : ''
          }`,
        },
      },
      {
        data: 'SID',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['SID'] ? columnsSearch['SID'] : ''
          }`,
        },
      },
      {
        data: 'PID',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['PID'] ? columnsSearch['PID'] : ''
          }`,
        },
      },
      {
        data: 'AccountName',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['Account Name']
              ? columnsSearch['Account Name']
              : ''
          }`,
        },
      },
      {
        data: 'SectionTitle',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['Section Title']
              ? columnsSearch['Section Title']
              : ''
          }`,
        },
      },
      {
        data: 'FormId',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['Form Id']
              ? columnsSearch['Form Id']
              : ''
          }`,
        },
      },
      {
        data: 'FormNotes',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['Form Notes']
              ? columnsSearch['Form Notes']
              : ''
          }`,
        },
      },
      {
        data: 'HelpText',
        name: '',
        searchable: true,
        orderable: true,
        search: {
          regex: false,
          value: `${
            columnsSearch && columnsSearch['Search Snippet']
              ? columnsSearch['Search Snippet']
              : ''
          }`,
        },
      },
    ],
    order: [
      {
        columnName: getSortKey(currentSortItem && currentSortItem.sortKey),
        dir: `${currentSortItem?.sortOrder}`,
      },
    ],
    search: searchList,
    IncludeGlobal: includeGlobal,
    IncludeInactive: includeInactive,
    IncludeHiddenAccount: includeHidden,
  };
}

function useGetAdvancedSectionSearch(
  params: QuestionSearchParams | undefined,
  columnsSearch?: Record<string, string>,
  currentSortItem?: SortItem,
): UseInfiniteQueryResult<TResult | undefined, TError> {
  const { searchList, includeGlobal, includeInactive, includeHidden } =
    params || {};
  const queryClient = useQueryClient();
  const data: TResult | undefined = queryClient.getQueryData([
    'advancedSection_search',
    {
      searchList,
      includeGlobal,
      includeInactive,
      includeHidden,
      columnsSearch,
      currentSortItem,
    },
  ]);

  let start = data && data.Data && data.Data.Data ? data.Data.Data.length : 1;

  if (start > 0 && data && data && data.Data !== undefined) {
    const totalPages = data?.Data?.FilteredRecords / pageLength;

    if (totalPages <= 1) {
      start = start + 1;
    } else {
      start = 1;
    }
  }
  return useInfiniteQuery(
    [
      'advancedSection_search',
      {
        searchList,
        includeGlobal,
        includeInactive,
        includeHidden,
        columnsSearch,
        currentSortItem,
      },
    ],
    () => {
      if (!params) return;
      const requestData = prepareData({
        start,
        ...params,
        columnsSearch,
        currentSortItem,
      });
      return debouncedFetch({
        requestData,
      });
    },
    {
      getNextPageParam: (
        lastPage: TResult | undefined,
        allPages: Array<TResult | undefined>,
      ) => {
        if (lastPage !== null && lastPage?.Data?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (
        firstPage: TResult | undefined,
        allPages: Array<TResult | undefined>,
      ) => {
        if (firstPage !== null && firstPage?.Data?.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetAdvancedSectionSearch };
